import {
  throwError as observableThrowError,
  Observable,
  Subscription,
} from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FirebaseStorageUrlResolver } from '../firebase/firebase-storage-url-resolver';
import { Guid } from 'guid-typescript';
import { AuthenticationService } from '../authentication/authentication.service';
import { ResourceUpload } from '../firebase/resource-upload';
import { FirebaseService } from '../firebase/firebase.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsDto } from '../../shared/notifications-data-dto';
import { extract } from '../i18n.service';
import { Logger } from '../logger.service';
import { CookieService } from 'ngx-cookie-service';
import { map, catchError, mergeMap } from 'rxjs/operators';
import { timeout } from 'rxjs/operators';
import { getDownloadURL } from 'firebase/storage';

const routes = {
  genVersion: '/api/genVersion',
  mapPrepareMap: '/api/mapPrepareMap',
  mapCreateWorkMapFromStorage: '/api/mapCreateWorkMapFromStorage',
  mapCreateWorkMapFromGoogleDrive: '/api/mapCreateWorkMapFromGoogleDrive',
  docSearchDocUserByDocid: '/api/docSearchDocUserByDocid',
  docInsertDocShared: '/api/docInsertDocShared',
  mapViewMap: '/api/mapViewMap',
  mapEditMap: '/api/mapEditMap',
  mapExportSme: '/api/mapExportSme',
  docInsertImageFromDrive: '/api/docInsertImageFromDrive',
  docRecentDocs: '/api/docRecentDocs',
  docRemoveRecent: '/api/docRemoveRecent',
  docTrashRecent: '/api/docTrashRecent',
  docStopSharing: '/api/docStopSharing',
  docRecentData: '/api/docRecentData',
  mapSaveSme: '/api/mapSaveSme',
  docRenameDoc: '/api/docRenameDoc',
  mapCreateNewMap: '/api/mapCreateNewMap',
  mapCreateNewDocument: '/api/mapCreateNewDocument',
  usrUserPreferences: '/api/usrUserPreferences',
  mapExportMapInGoogleDrive: '/api/mapExportMapInGoogleDrive',
  docUploadImageFromUri: '/api/docUploadImageFromUri',
  usrSetSessionId: '/api/usrSetSessionId',
  usrCheckSessionId: '/api/usrCheckSessionId',
  usrWriteGoogleUserData: '/api/usrWriteGoogleUserData',
  usrUnlinkGoogleEmailForUser: '/api/usrUnlinkGoogleEmailForUser',
  usrSetUserInfo: '/api/usrSetUserInfo',
  // usrHasUserAcceptedPrivacy: '/api/usrHasUserAcceptedPrivacy',
  docGetShareData: '/api/docGetShareData',
  docDeleteChat: '/api/docDeleteChat',
  mapDeleteMapEvents: '/api/mapDeleteMapEvents',
  notAdminInsertNotification: '/api/notAdminInsertNotification',
  notAdminListNotifications: '/api/notAdminListNotifications',
  notUserListNotifications: '/api/notUserListNotifications',
  notSetUserNotificationsShownState: '/api/notSetUserNotificationsShownState',
  staAddStatRecords: '/api/staAddStatRecords',
  staAddCustomDocStat: '/api/staAddCustomDocStat',
  staReadSingleUserStats: '/api/staReadSingleUserStats',
  staReadOrderStatsStep1: '/api/staReadOrderStatsStep1',
  staReadOrderStatsStep2: '/api/staReadOrderStatsStep2',
  staReadOrderStatsStep3: '/api/staReadOrderStatsStep3',
  staReadOrderStatsStep4: '/api/staReadOrderStatsStep4',
  staFreezeSubscription: '/api/staFreezeSubscription',
  staGetStatsByOriginAndYearStep1: '/api/staGetStatsByOriginAndYearStep1',
  staGetStatsByOriginAndYearStep2: '/api/staGetStatsByOriginAndYearStep2',
  staGetStatsByOriginAndYearStep3: '/api/staGetStatsByOriginAndYearStep3',
  staGetStatsByOriginAndYearStep4: '/api/staGetStatsByOriginAndYearStep4',
  staGetStatsByOriginAndYearStep5: '/api/staGetStatsByOriginAndYearStep5',
  staGetDomainsStatsByYear: '/api/staGetDomainsStatsByYear',
  docFindDocSharedInfo: '/api/docFindDocSharedInfo',
  docFindOwnerEmailByDocId: '/api/docFindOwnerEmailByDocId',
  subDeleteSubscription: '/api/subDeleteSubscription',
  docFindDocLinkInfo: '/api/docFindDocLinkInfo',
  usrAddUserToNewsletter: '/api/usrAddUserToNewsletter',
  subActivateDemo: '/api/subActivateDemo',
  // subUnlockOrders: '/api/subUnlockOrders',
  usrLoggedIn: '/api/usrLoggedIn',
  usrRevokeToken: '/api/usrRevokeToken',
  usrGetAccessToken: '/api/usrGetAccessToken',
  usrGetAuthUser: '/api/usrGetAuthUser',
  matLatex2Image: '/api/matLatex2Image',
  usrGetUserDriveQuota: '/api/usrGetUserDriveQuota',
  matGetOcrMathpix: '/api/matGetOcrMathpix',
  matGetOcrMathpixScanCount: '/api/matGetOcrMathpixScanCount',
  matGetOcrMathpixScansLeft: '/api/matGetOcrMathpixScansLeft',
  usrDeleteUserByEmail: '/api/usrDeleteUserByEmail',
  usrListUsers: '/api/usrListUsers',
  docWriteShares: '/api/docWriteShares',
  docUploadDocToDrive: '/api/docUploadDocToDrive',
  docEmptyTrash: '/api/docEmptyTrash',
  docAddDocMetadata: '/api/docAddDocMetadata',
  docFindDocInfoByDocId: '/api/docFindDocInfoByDocId',
  docChangeDocLanguage: '/api/docChangeDocLanguage',

  resourcePath: () => `/docs`,
  resourceDoc: (filename: string) => `${routes.resourcePath()}/${filename}`,
  resourceSmePath: (docId: string) =>
    `${routes.resourcePath()}/myDoc/${docId}.sme`,
  workPath: (docId: string) => `${routes.resourcePath()}/${docId}`,
  workJson: (docId: string) => `${routes.workPath(docId)}/map.json`,
  workMapImage: (docId: string) => `${routes.workPath(docId)}/document.jpg`,
};

const logger: Logger = new Logger('ApiService');
export interface Config {
  readOnly: boolean;
  docId: string;
  docType: string;
  googleId: string;
  data: any;
  html: string;
  userId: string;
  filedata: any;
  result: any;
  recents: any;
  mapJson: any;
  forceLogin: boolean;
  access_token: boolean;
  storageRef: string;
  ownerEmail: string;
  userEmail: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService implements OnDestroy {
  private uploadFileSubscription: Subscription | undefined;
  private uploadWorkResourceSubscription: Subscription | undefined;
  private addPDfToRecentsSubscription: Subscription | undefined;
  private getRecentDataSubscription: Subscription | undefined;
  private sessionId = '';

  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService,
    private cookieService: CookieService
  ) {
    console.log('ApiService constructor');
  }

  ngOnDestroy() {
    if (this.uploadFileSubscription) {
      this.uploadFileSubscription.unsubscribe();
    }
    if (this.uploadWorkResourceSubscription) {
      this.uploadWorkResourceSubscription.unsubscribe();
    }
  }

  // private getIdTokenForFunctions(funcName: string): string {

  //     const { GoogleAuth } = require('google-auth-library');
  //     const auth = new GoogleAuth();

  // }

  public version(): any {
    return this.http.post<Response>(routes.genVersion, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public hasUserEnoughDriveQuota(bytesToSave: number = 5000000): any {
    return new Promise((onResolve, onError) => {
      this.getUserDriveQuota().subscribe({
        next: (quota: any) => {
          console.log(JSON.stringify(quota));

          const limit =
            quota.storageQuota.limit != undefined
              ? Number(quota.storageQuota.limit)
              : -1;
          const usage =
            quota.storageQuota.usage != undefined
              ? Number(quota.storageQuota.usage)
              : -1;

          //unlimited account
          if (limit != -1) {
            const availableSpace = limit - usage;
            onResolve(availableSpace >= bytesToSave);
          } else {
            onResolve(true);
          }
        },
        error: (error: any) => {
          console.log(error);
          onError(error);
        },
      });
    });
  }

  //Get Google Drive Quota
  public getUserDriveQuota(): any {
    return this.http.get(routes.usrGetUserDriveQuota, {}).pipe(
      map((res: any) => {
        return res;
        //esempio {"storageQuota":{"limit":"109951162777600","usage":"1585734447005","usageInDrive":"1632926463","usageInDriveTrash":"0"}}
      }),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public getAccessToken(): any {
    return this.http.post<Config>(routes.usrGetAccessToken, {}, {}).pipe(
      map((res: Config) => res.access_token),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public getAccessTokenPromise(): any {
    // return this.getAccessToken().toPromise();
    return new Promise((resolve, reject) => {
      this.getAccessToken().subscribe(
        (_accessToken: any) => {
          return resolve(_accessToken);
        },
        (err: any) => {
          return reject(err);
        }
      );
    });
  }

  public getAuthUser(): any {
    return this.http.post(routes.usrGetAuthUser, {}, {}).pipe(
      map((res: any) => res.authUser),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public getAuthUserPromise(): any {
    return new Promise((resolve, reject) => {
      this.getAuthUser().subscribe(
        (authUser: any) => {
          return resolve(authUser);
        },
        (err: any) => {
          return reject(err);
        }
      );
    });
  }

  public revokeToken(accessToken: string): any {
    const body = { accessToken: accessToken };

    return this.http.post<Response>(routes.usrRevokeToken, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public revokeTokenPromise(accessToken: string): any {
    return new Promise((resolve, reject) => {
      this.revokeToken(accessToken).subscribe(
        (res: any) => {
          return resolve(res);
        },
        (err: any) => {
          return reject(err);
        }
      );
    });
  }

  private getImageName(
    docId: string,
    nodeId: string,
    name: string,
    storagePath?: string,
    fromServer: boolean = false
  ): string {
    //   const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
    const guid = Guid.create();
    // const extension: string = name.split('.').pop(); --> JHUBA molte non ce l'hanno, ma funzionano lo stesso
    if (storagePath) {
      return storagePath;
    } else {
      //   if (userId) {
      let p = routes.workPath(docId) + `/${nodeId}_${guid}.png`;
      if (fromServer) {
        p = p.substring(1);
      }
      console.log('storagepath ' + p);
      return p; // /${extension}`;
      // } else {
      //     return '';
      // }
    }
  }

  public uploadImageFromUri(
    uri: string,
    docId: string,
    nodeId: string,
    storagePath?: string
  ): Observable<any> {
    const destPath = this.getImageName(docId, nodeId, uri, storagePath, true);
    const bucketName = this.firebaseService.getBucketName();

    const body = {
      bucket: bucketName,
      uri: uri,
      destPath: destPath,
    };
    return this.http
      .post<Response>(routes.docUploadImageFromUri, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public insertImageFromDrive(
    googleId: string,
    docId: string,
    nodeId: string,
    storagePath?: string
  ): Observable<any> {
    const destPath = this.getImageName(docId, nodeId, '', storagePath, true);
    const bucketName = this.firebaseService.getBucketName();
    const body = {
      googleId: googleId,
      bucket: bucketName,
      destPath: destPath,
      docId: docId,
    };
    return this.http
      .post<Response>(routes.docInsertImageFromDrive, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public insertImageFromSvg(
    svg: string,
    docId: string,
    nodeId: string,
    storagePath?: string
  ): Observable<any> {
    return new Observable<any>((observer: any) => {
      const destPath = this.getImageName(docId, nodeId, '', storagePath);
      this.firebaseService
        .uploadStringPromise(svg, destPath, 'image/svg+xml')
        .then((ref: any) => {
          getDownloadURL(ref).then(
            (url: string) => {
              observer.next({ url: url, destPath: destPath });
              observer.complete();
            },
            (error: any) => {
              logger.error('FireBase Storage NOT FOUND: ' + error.message);
              observer.next({ url: '', destPath: '' });
              observer.complete();
            }
          );
        });
    });
  }

  // public addPDfToRecents(pdfId: string, name: string, googleId: string, color: string, lastUpdated: string, lastOpened: string, imported: string, storageRef: string, link: string): Observable<any> {
  //     // const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //     //  const resourcePdfPath: string = (userId) ? routes.resourcePdfPath(userId, pdfId) : '';

  //     const body = {
  //         googleId: googleId,
  //         pdfId: pdfId,
  //         name: name,
  //         lastOpened: lastOpened,
  //         lastUpdated: lastUpdated,
  //         imported: imported,
  //         color: color,
  //         storageRef: storageRef,
  //         link: link
  //     };
  //     return this.http.post<Config>(routes.apiAddPdfToRecents, body, {})
  //         .pipe(
  //             map((res: Config) => res),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));

  // }

  // public deleteFromPdf(pdfId: string): Observable<any> {
  //     const body = {
  //         pdfId: pdfId
  //     }
  //     return this.http.post<Response>(routes.apiDeleteFromPdf, body, {})
  //         .pipe(
  //             map((res: Response) => res), catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));

  // }

  // public deletePdfFromMaps(mapId: string): Observable<any> {
  //     const body = {
  //         mapId: mapId
  //     };
  //     return this.http.post<Response>(routes.apiDeletePdfFromMaps, body, {})
  //         .pipe(
  //             map((res: Response) => res), catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));
  // }

  // public updatePdfMap(pdfId: string, mapId: string): Observable<any> {
  //     // const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //     //  const resourcePdfPath: string = (userId) ? routes.resourcePdfPath(userId, pdfId) : '';

  //     const body = {
  //         mapId: mapId,
  //         pdfId: pdfId
  //     };
  //     return this.http.post<Response>(routes.apiUpdatePdfMap, body, {})
  //         .pipe(
  //             map((res: Response) => res), catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));

  // }

  // public uploadPDF(pdfUpload: ResourceUpload, pdfId: string): Observable<any> {
  //     const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //     const resourcePdfPath: string = (userId) ? routes.resourcePdfPath(userId, pdfId) : '';
  //     return new Observable<any>((observer: any) => {
  //         const name = pdfUpload.file.name.replace(/\.[^/.]+$/, '') + '.png'; // filename no ext + png

  //         if (this.uploadFileSubscription) { this.uploadFileSubscription.unsubscribe(); }
  //         this.uploadFileSubscription = this.firebaseService.uploadFile(pdfUpload, resourcePdfPath)
  //             .subscribe({
  //                 next: (ref: any) => {
  //                     // progress
  //                     if (ref >= 0 && ref <= 100) {
  //                         observer.next(ref);
  //                     } else {
  //                         getDownloadURL(ref)
  //                             .then((url: string) => {
  //                                 observer.next({ url: url, destPath: ref });
  //                                 observer.complete();
  //                             }, (error: any) => {
  //                                 logger.error('FireBase Storage NOT FOUND: ' + error.message);
  //                                 observer.next({ url: '', destPath: '' });
  //                                 observer.complete();
  //                             });
  //                     }
  //                 },
  //                 error: (err) => {
  //                     logger.error('upload PDF ERROR:' + err);
  //                     observer.next({ url: '', destPath: '' });
  //                     observer.complete();
  //                 },
  //                 complete: () => {

  //                 }

  //             });
  //     });
  // }

  // public uploadPDFBlob(blob: Blob, name: string, mapId: string): Observable<any> {
  //     const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //     let resourcePdfPath: string = (userId) ? routes.resourcePdfPath(userId) : '';
  //     const path = require('node:path')

  //     resourcePdfPath = path.join(resourcePdfPath, name);

  //     return new Observable<any>((observer: any) => {

  //         if (this.uploadFileSubscription) { this.uploadFileSubscription.unsubscribe(); }
  //         this.uploadFileSubscription = this.firebaseService.uploadBlob(blob, resourcePdfPath)
  //             .subscribe({
  //                 next: (ref: any) => {
  //                     // progress
  //                     if (ref >= 0 && ref <= 100) {
  //                         // observer.next(ref); NON GESTITO PROGRESS
  //                     } else {
  //                         getDownloadURL(ref)
  //                             .then((url: string) => {
  //                                 observer.next({ url: url, destPath: resourcePdfPath });
  //                                 observer.complete();
  //                             }, (error: any) => {
  //                                 logger.error('FireBase Storage NOT FOUND: ' + error.message);
  //                                 observer.next({ url: '', destPath: '' });
  //                                 observer.complete();
  //                             });
  //                     }
  //                 },
  //                 error: (err) => {
  //                     logger.error('upload PDF ERROR:' + err);
  //                     observer.next({ url: '', destPath: '' });
  //                     observer.complete();
  //                 },
  //                 complete: () => {

  //                 }

  //             });
  //     });
  // }

  // public downloadPdfFromDrive(googleId: string, filename: string, docId: string): Observable<any> {
  //     const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //     const destPath = (userId) ? routes.resourcePdfPath(userId, filename) : '';
  //     const body = {
  //         googleId: googleId,
  //         destPath: destPath
  //     };
  //     return this.http.post(routes.apiDownloadPdfFromDrive, body, { responseType: 'blob' })
  //         .pipe(
  //             map(
  //                 (res: Blob) => res
  //             ),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));
  // }

  // public savePDF(googleId: string, filePath: string, pdfName?: string) {
  //     const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //     const body = {
  //         userId: userId,
  //         googleId: googleId,
  //         pdfName: pdfName,
  //         filePath: filePath
  //     };
  //     return this.http.post<Config>(routes.apiSavePdf, body, {})
  //         .pipe(
  //             map(
  //                 (res: Config) => {
  //                     return res.googleId;
  //                 }),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));
  // }

  // public savePDFasBlob(googleId: string, googleFolderId: string, blob: Blob, pdfName?: string) {

  //     const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //     const body = {

  //         userId: userId,
  //         googleId: googleId,
  //         pdfName: pdfName,
  //         pdfBlob: blob,
  //         googleFolderId: googleFolderId

  //     };
  //     return this.http.post<Config>(routes.apiSavePdfasBlob, body, {})
  //         .pipe(
  //             map(
  //                 (res: Config) => {
  //                     return res.googleId;
  //                 }),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));

  // }

  // public uploadPDFasBlob(blob: Blob, pdfId: string) {
  //     let pdfPath: StorageReference;
  //     return new Observable<any>((observer: any) => {
  //         const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //         const resourcePdfPath: string = (userId) ? routes.resourcePdfPath(userId, pdfId) : '';
  //         this.firebaseService.uploadBlob(blob, resourcePdfPath).subscribe({
  //             next: (_ref) => {
  //                 if (_ref >= 0 && _ref <= 100) {
  //                     // observer.next(ref); NON GESTITO PROGRESS
  //                 } else {
  //                     pdfPath = _ref;

  //                 }

  //             },
  //             complete: () => {
  //                 getDownloadURL(pdfPath)
  //                     .then((url: string) => {
  //                         observer.next({ url: url, destPath: pdfPath });
  //                         observer.complete();
  //                     }, (error: any) => {
  //                         logger.error('FireBase Storage NOT FOUND: ' + error.message);
  //                         observer.next({ url: '', destPath: '' });
  //                         observer.complete();
  //                     });
  //             },
  //             error: (error) => {
  //                 logger.error('FireBase Storage NOT FOUND: ' + error.message);
  //                 observer.next('');
  //                 observer.complete();

  //             }
  //         });
  //     });

  // }

  // public getPdfRecents(): any {
  //     return this.http.post<Config>(routes.apiGetPdfRecents, {}, {})
  //         .pipe(
  //             map(
  //                 (res: Config) => res.recents
  //             ),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));

  // }

  // public getPdfFromStorage(storageRef: string): Observable<any> {
  //     return new Observable<any>((observer: any) => {
  //         const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //         //const destPath = (userId) ? routes.resourcePdfPath(userId) : '';
  //         this.firebaseService.getDownloadUri(storageRef).then((url) => {
  //             if (url !== '') {
  //                 this.firebaseService.downloadResourceAsBlob(storageRef, 'application/pdf').subscribe({
  //                     next: (pdfBlob) => {
  //                         observer.next(pdfBlob);
  //                         observer.complete();

  //                     },
  //                     error: (err) => {
  //                         logger.error('insertImage ERROR:' + err);
  //                         observer.error({ url: '', destPath: '' });
  //                         observer.complete();
  //                     }
  //                 });
  //             } else {
  //                 observer.error('');
  //                 observer.complete();

  //             }
  //         }).catch(() => {
  //             observer.error('');
  //             observer.complete();
  //         });
  //     });

  // }

  // private checkPdfMaps(mapId: string): Observable<any> {

  //     //  const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //     const body = {
  //         mapId: mapId
  //     };
  //     return this.http.post<Config>(routes.apiGetPdfFromMap, body, {})
  //         .pipe(
  //             map(
  //                 (res: Config) => {
  //                     return res.pdfId;
  //                 }),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));

  // }

  // public getPdfStorageById(pdfId: string): Observable<any> {
  //     const body = {
  //         pdfId: pdfId
  //     };
  //     return this.http.post<Config>(routes.apiGetPdfStorageById, body, {})
  //         .pipe(
  //             map(
  //                 (res: Config) => {
  //                     return { storageRef: res.data.storageRef, name: res.data.name, color: res.data.color };
  //                 }),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));

  // }

  // public checkPdfToLoad(mapId: string): Observable<any> {
  //     return new Observable<any>((observer: any) => {
  //         const userId = this.authenticationService.reloadCredentials()?.firebaseUserId;
  //         this.checkPdfMaps(mapId).subscribe({
  //             next: (_pdfId: string) => {
  //                 const pdfId = _pdfId;
  //                 const destPath = (userId) ? routes.resourcePdfPath(userId, pdfId) : '';
  //                 if (pdfId !== null && pdfId != '') {
  //                     this.getPdfStorageById(pdfId).subscribe({
  //                         next: (data) => {
  //                             if (data !== '') {
  //                                 // this.firebaseService.downloadResourceFromStorageAsBlob(storageRef, 'application/pdf').subscribe({
  //                                 //   next: (pdfBlob) => {
  //                                 observer.next({ storageRef: data.storageRef, pdfId: pdfId, name: data.name, color: data.color });
  //                                 observer.complete();

  //                                 //   //  },
  //                                 //     error: (err) => {
  //                                 //         logger.error('insertImage ERROR:' + err);
  //                                 //         observer.error({ url: '', destPath: '' });
  //                                 //         observer.complete();
  //                                 //     }
  //                                 // });
  //                             } else {
  //                                 observer.next('');
  //                                 observer.complete();

  //                             }
  //                         }, error: () => {
  //                             observer.error('');
  //                             observer.complete();
  //                         }
  //                     });
  //                 } else {
  //                     observer.next('');
  //                     observer.complete();

  //                 }

  //             },
  //             error: (error) => {
  //                 observer.error(error);
  //                 observer.complete();
  //             }
  //         });
  //     });
  // }

  // public downloadPDf(resourceFullPath: string): Observable<Blob> {

  //     return this.firebaseService.downloadResourceAsBlob(resourceFullPath, 'application/pdf');

  // }

  public insertImage(
    imageUpload: ResourceUpload,
    docId: string,
    nodeId: string
  ): Observable<any> {
    return new Observable<any>((observer: any) => {
      const name = imageUpload.file.name.replace(/\.[^/.]+$/, '') + '.png'; // filename no ext + png
      const destPath = this.getImageName(docId, nodeId, name);
      if (this.uploadFileSubscription) {
        this.uploadFileSubscription.unsubscribe();
      }
      this.uploadFileSubscription = this.firebaseService
        .uploadFile(imageUpload, destPath)
        .subscribe({
          next: (ref: any) => {
            // progress
            if (ref >= 0 && ref <= 100) {
              // observer.next(ref); NON GESTITO PROGRESS
            } else {
              getDownloadURL(ref).then(
                (url: string) => {
                  observer.next({ url: url, destPath: destPath });
                  observer.complete();
                },
                (error: any) => {
                  logger.error('FireBase Storage NOT FOUND: ' + error.message);
                  observer.next({ url: '', destPath: '' });
                  observer.complete();
                }
              );
            }
          },
          error: (err) => {
            logger.error('insertImage ERROR:' + err);
            observer.next({ url: '', destPath: '' });
            observer.complete();
          },
          complete: () => { },
        });
    });
  }

  public downloadMap(resourceFullPath: string): Observable<any> {
    return this.firebaseService.downloadResource(resourceFullPath, this.authenticationService.isAuthenticated());
  }

  public uploadMap(upload: ResourceUpload, docId?: string): Observable<any> {
    let resourceSmePath = '';
    if (docId) {
      resourceSmePath = routes.resourceSmePath(docId);
    } else {
      resourceSmePath = routes.resourceDoc(upload.file.name);
    }

    return this.firebaseService.uploadFile(upload, resourceSmePath);
  }

  /**
   *
   * param docId L'id del documento corrente
   * param filename Il nome del file di cui si vuole l'URL (oppure il suo path relativo partendo da work)
   * returns Una promise che contiene l'URL che ha nello Storage di Firebase la risorsa filename
   * dentro la cartella work dell'utente corrente.
   */
  public resolveWorkUrl(docId: string, filename: string): Promise<any> {
    let userId;
    // if (this.authenticationService.isAuthenticated()) {
    //     userId = this.authenticationService.credentials?.firebaseUserId;
    // }
    const storageUrl = `${routes.workPath(docId)}/${filename}`;
    return this.firebaseService.resolveStorageUrl(storageUrl);
  }

  public createNewMap(): Observable<string> {
    const mapName = this.translateService.instant(extract('NEW_MAP_NAME'));
    const bucketName = this.firebaseService.getBucketName();
    const body = {
      bucket: bucketName,
      mapName: mapName,
    };
    return this.http.post<Config>(routes.mapCreateNewMap, body, {}).pipe(
      map((res: Config) => res.docId),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public createNewGoogleDocument(
    name: string,
    json: any,
    locale: any,
    notes: any,
    docId: string,
    googleFolderId: string,
    googleDocFileId: string
  ): Observable<string> {
    const bucketName = this.firebaseService.getBucketName();
    const body = {
      format: 'google',
      name: name,
      googleFolderId: googleFolderId,
      bucket: bucketName,
      docid: docId,
      json: json,
      locale: locale,
      notes: notes,
      googleDocFileId: googleDocFileId,
    };
    return this.http.post<Config>(routes.mapCreateNewDocument, body, {}).pipe(
      map((res: Config) => res.googleId),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public createNewDocument(
    format: string,
    name: string,
    json: any,
    locale: any,
    notes: any,
    docId: string
  ): Observable<any> {
    const bucketName = this.firebaseService.getBucketName();
    const body = {
      format: format,
      name: name,
      bucket: bucketName,
      docid: docId,
      json: json,
      locale: locale,
      notes: notes,
    };
    if (format === 'docx-ms' || format === 'docx-lo') {
      // Docx
      return this.http
        .post(routes.mapCreateNewDocument, body, { responseType: 'blob' })
        .pipe(
          map((res: Blob) => res),
          catchError((error: any) => {
            logger.error(error);
            return observableThrowError(() => error);
          })
        );
    } else {
      // PDF
      return this.http.post<Config>(routes.mapCreateNewDocument, body, {}).pipe(
        map((res: Config) => res.html),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
    }
  }

  public exportMapToGDrive(
    googleFolderId: string,
    name: string,
    docId: string
  ): Observable<string> {
    const bucketName = this.firebaseService.getBucketName();
    const body = {
      mapName: name,
      googleFolderId: googleFolderId,
      bucket: bucketName,
      docId: docId,
    };
    return this.http
      .post<Config>(routes.mapExportMapInGoogleDrive, body, {})
      .pipe(
        map((res: Config) => res.googleId),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public prepareMap(docId: string) {
    const bucketName = this.firebaseService.getBucketName();
    const resPath: string = routes.resourcePath();

    const body = {
      docid: docId,
      bucket: bucketName,
      resPath: resPath,
    };
    return this.http.post<Config>(routes.mapPrepareMap, body, {}).pipe(
      map((res: Config) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }
  public exportSmeInGDrive(
    googleFolderId: string,
    blob: Blob
  ): Observable<string> {
    const body = {
      blob: blob,
      googleFolderId: googleFolderId,
    };
    return this.http
      .post<string>(routes.mapExportMapInGoogleDrive, body, {})
      .pipe(
        map((res: string) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public createWorkMapFromStorage(
    fileRef: any,
    mapName: string,
    docId: string
  ): Observable<string> {
    // const googleUserId = this.authenticationService.reloadCredentials()?.googleUserId;
    const body = {
      bucket: fileRef.bucket,
      // googleUserId: googleUserId,
      filePath: fileRef.fullPath,
      mapName: mapName,
      docId: docId,
      // googleFolderId: googleFolderId
    };
    return this.http
      .post<Config>(routes.mapCreateWorkMapFromStorage, body, {})
      .pipe(
        map((res: Config) => res.docId),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public createWorkMapFromGoogleDrive(
    fileId: string,
    googleUserId?: string
  ): Observable<string> {
    const bucketName = this.firebaseService.getBucketName();
    const resPath: string = routes.resourcePath();
    const body = {
      fileId: fileId,
      bucket: bucketName,
      resPath: resPath,
    };
    return this.http
      .post<string>(routes.mapCreateWorkMapFromGoogleDrive, body, {})
      .pipe(
        map((res: string) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }


  public downloadJsonToView(docId: string, withCredentials: boolean) {
    const bucketName = this.firebaseService.getBucketName();
    const body = {
      bucket: bucketName,
      docId: docId,
    };
    const options = withCredentials
      ? {}
      : { headers: new HttpHeaders({ Authorization: '' }) };
    return this.http.post<Config>(routes.mapViewMap, body, options).pipe(
      map((res: Config) => res.data),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public downloadJsonToEdit(docId: string) {
    const bucketName = this.firebaseService.getBucketName();
    const body = {
      bucket: bucketName,
      docId: docId,
    };
    return this.http.post<Config>(routes.mapEditMap, body, {}).pipe(
      map((res: Config) => res.data),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public searchForDoc(docId: string): Observable<string> {
    const body = {
      docId: docId,
    };
    return this.http
      .post<Config>(routes.docSearchDocUserByDocid, body, {})
      .pipe(
        map((res: Config) => res.ownerEmail),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public insertMapShared(docId: string, shareType: string): Observable<string> {
    const body = {
      docId: docId,
      shareType: shareType,
    };
    return this.http.post<string>(routes.docInsertDocShared, body, {}).pipe(
      map((res: string) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public uploadWorkJson(docId: string, resource: any) {
    const jsonResource: string = routes.workJson(docId);
    return this.firebaseService.fsUploadString(resource, jsonResource);
  }

  public uploadWorkResource(docId: string, resource: any, name: string) {
    if (resource) {
      const workResource: string = routes.workPath(docId) + '/' + name;
      return this.firebaseService.uploadBase64(
        resource,
        workResource,
        'image/jpg'
      );
    } else {
      return new Observable((observer: any) => {
        observer.next('');
        observer.complete();
      });
    }
  }

  public getUrlResolver(): FirebaseStorageUrlResolver {
    return new FirebaseStorageUrlResolver(this.firebaseService);
  }

  public saveMapRequest(
    docId: string,
    mapWorkJson: any,
    mapOperationsData: any
  ): Observable<any> {
    this.authenticationService.reloadCredentials();
    // const googleUserId = this.authenticationService.credentials?.googleUserId;
    // const userId = this.authenticationService.credentials?.firebaseUserId;
    // const saveToDrive = this.authenticationService.credentials?.providerId === 'google.com';
    const bucketName = this.firebaseService.getBucketName();
    let mapName = '';
    try {
      const workjson = JSON.parse(mapWorkJson);
      mapName = workjson.mapProps.mapName;
    } catch (error) {
      logger.error(error);
      return observableThrowError(() => error);
    }
    const body = {
      docId: docId,
      saveAs: false,
      bucket: bucketName,
      mapName: mapName,
      saveToDrive: false, //saveToDrive,
      mapWorkJson: mapWorkJson,
      operations: mapOperationsData,
    };

    return this.http
      .post(routes.mapSaveSme, body, { responseType: 'blob' })
      .pipe(
        map((res: Blob) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public saveMap(
    docId: string,
    imgPreview: any,
    imgMap: any,
    mapWorkjson: any,
    mapOperationsData: any
  ): Observable<any> {
    return new Observable((observer: any) => {
      if (this.uploadWorkResourceSubscription) {
        this.uploadWorkResourceSubscription.unsubscribe();
      }
      this.findOwnerEmailByDocId(docId).subscribe((result: any) => {
        const ownerEmail = result.ownerEmail;
        this.uploadWorkResourceSubscription = this.uploadWorkResource(
          docId,
          imgPreview,
          'preview.jpg'
        )
          .pipe(
            mergeMap(() => this.uploadWorkResource(docId, imgMap, 'document.jpg'))
          )
          .pipe(mergeMap(() => this.uploadWorkJson(docId, mapWorkjson)))
          .pipe(
            mergeMap(() =>
              this.saveMapRequest(docId, mapWorkjson, mapOperationsData)
            )
          )
          .subscribe({
            next: (blob: Blob) => {
              observer.next(blob);
              observer.complete();
            },
            error: (error: any) => {
              observer.error(error);
            },
          });
      });
    });
  }

  public exportSmeRequest(docId: string): Observable<any> {
    const body = {
      bucket: this.firebaseService.getBucketName(),
      docid: docId,
    };
    return this.http
      .post(routes.mapExportSme, body, { responseType: 'blob' })
      .pipe(
        map((res: Blob) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public exportSme(
    docId: string,
    imgPreview: any,
    imgMap: any,
    mapWorkjson: any
  ): Observable<any> {
    return new Observable((observer: any) => {
      if (this.uploadWorkResourceSubscription) {
        this.uploadWorkResourceSubscription.unsubscribe();
      }
      this.findOwnerEmailByDocId(docId).subscribe((result: any) => {
        const ownerEmail = result.ownerEmail;
        this.uploadWorkResourceSubscription = this.uploadWorkResource(
          docId,
          imgPreview,
          'preview.jpg'
        )
          .pipe(
            mergeMap(() => this.uploadWorkResource(docId, imgMap, 'document.jpg'))
          )
          .pipe(mergeMap(() => this.uploadWorkJson(docId, mapWorkjson)))
          .pipe(mergeMap(() => this.exportSmeRequest(docId)))
          .subscribe({
            next: (sme: any) => {
              observer.next(sme);
              observer.complete();
            },
            error: (error: any) => {
              observer.error(error);
              observer.complete();
            },
          });
      });
    });
  }

  public renameDoc(docId: any, newName: string): Observable<any> {
    const body = { docId: docId, docName: newName };
    return this.http.post<Response>(routes.docRenameDoc, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public awaitableGetRecentData(email: string, docInfo: any) {
    return new Promise((resolve, reject) => {
      if (this.getRecentDataSubscription) {
        this.getRecentDataSubscription.unsubscribe();
      }
      this.getRecentDataSubscription = this.getRecentData(
        email,
        docInfo
      ).subscribe((data: any) => {
        return resolve(data);
      });
    });
  }

  public getRecentData(email: string, docInfo: any) {
    const body = { email: email, docInfo: docInfo };
    return this.http.post<Response>(routes.docRecentData, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public getRecentDocs(): any {
    const bucketName = this.firebaseService.getBucketName();
    const body = { bucket: bucketName };
    return this.http.post<Config>(routes.docRecentDocs, body, {}).pipe(
      map((res: Config) => res.recents),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public getUserPreferences(groupName: string): any {
    const body = {
      groupName: groupName,
    };
    return this.http.post<Response>(routes.usrUserPreferences, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public updateUserPreference(groupName: string, userPrefs: any) {
    const body = {
      groupName: groupName,
      userPrefs: userPrefs,
    };
    return this.http.post<Response>(routes.usrUserPreferences, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public removeRecent(docId: string): any {
    const bucketName = this.firebaseService.getBucketName();
    const body = { docId: docId, bucket: bucketName };
    return this.http.post<Response>(routes.docRemoveRecent, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public trashRecent(docId: string, operation: 'trash' | 'restore'): any {
    const trash = operation === 'trash' ? true : false;
    const body = { docId: docId, trash: trash };
    return this.http.post<Response>(routes.docTrashRecent, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public stopSharing(docId: string): any {
    const body = {
      docId: docId,
    };
    return this.http.post<Response>(routes.docStopSharing, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  private getBaseName(str: string) {
    let base = str.substring(str.lastIndexOf('/') + 1);
    if (base.lastIndexOf('.') !== -1) {
      base = base.substring(0, base.lastIndexOf('.'));
    }
    return base;
  }

  // public getWhitelist(): any {
  //     const body = {};
  //     return this.http.post<Response>(routes.apiGetWhitelist, body, {})
  //         .pipe(
  //             map(
  //                 (res: Response) => res
  //             ),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));
  // }

  // public setWhitelist(whitelist: string[]): any {
  //     const body = { emails: whitelist };
  //     return this.http.post<Response>(routes.apiSetWhitelist, body, {})
  //         .pipe(
  //             map(
  //                 (res: Response) => res
  //             ),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));
  // }

  public writeGoogleUserData(googleUserData: any) {
    const body = {
      googleUserData: googleUserData,
    };
    return this.http
      .post<Response>(routes.usrWriteGoogleUserData, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public unlinkGoogleEmailForUser() {
    return this.http
      .post<Response>(routes.usrUnlinkGoogleEmailForUser, {}, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public setUserInfo(
    userEmail: string,
    googleUserEmail: string,
    googleUserId: string,
    fullName: string,
    lastLogin: string,
    privacyAccepted?: string,
    newsletter?: boolean,
    userType?: string,
    ageRange?: string
  ) {
    const body = {
      userEmail: userEmail,
      googleUserEmail: googleUserEmail,
      googleUserId: googleUserId,
      fullName: fullName,
      lastLogin: lastLogin,
      privacyAccepted: privacyAccepted,
      newsletter: newsletter,
      userType: userType,
      ageRange: ageRange,
    };
    return this.http.post<Response>(routes.usrSetUserInfo, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  // public hasUserAcceptedPrivacy(email: string) {
  //   const body = {
  //     userEmail: email,
  //   };

  //   return this.http
  //     .post<Response>(routes.usrHasUserAcceptedPrivacy, body, {})
  //     .pipe(
  //       map((res: Response) => res),
  //       catchError((error: any) => {
  //         logger.error(error);
  //         return observableThrowError(() => error);
  //       })
  //     );
  // }

  public setSessionId(sessionId: string) {
    const body = { sessionId: sessionId };
    this.sessionId = sessionId;
    return this.http.post<Response>(routes.usrSetSessionId, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public getSessionId(): string {
    if (this.sessionId === '') {
      const cookies = this.cookieService.getAll();
      const sessionCookie = this.cookieService.get('smxsession');
      if (sessionCookie) {
        this.sessionId = sessionCookie;
      } else {
        this.sessionId = Guid.create().toString();
        this.cookieService.set('smxsession', this.sessionId, 100, '/');
      }
    }
    return this.sessionId;
  }

  public checkSessionId() {
    if (!window.location.href.includes('web.smxstudio.it')) {
      return new Observable<any>((observer) => {
        const res = {
          ok: true,
          result: true,
        };
        observer.next(res);
        observer.complete();
      });
    }

    const body = { sessionId: this.getSessionId() };

    return this.http.post<Response>(routes.usrCheckSessionId, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        return observableThrowError(() => error);
      })
    );
  }

  // public getIntroMaps(): any {
  //     const body = {};
  //     return this.http.post<Response>(routes.apiGetIntroMaps, body, {})
  //         .pipe(
  //             map(
  //                 (res: Response) => res
  //             ),
  //             catchError((error: any) => {
  //                 logger.error(error);
  //                 return observableThrowError(() => error);
  //             }));
  // }

  public getShareData(
    email: string,
    docId: string
  ): any {
    if (!email) email = '';
    const body = {
      email: email,
      docId: docId,
    };
    const options = { headers: new HttpHeaders({ Authorization: '' }) };
    return this.http.post<Response>(routes.docGetShareData, body, options).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  // NOTIFICHE

  public userListNotifications(expireDateSubscription: any) {
    const body = {
      expireDate: expireDateSubscription,
    };
    return this.http
      .post<Response>(routes.notUserListNotifications, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public setUserNotificationsShownState(shown: boolean) {
    const body = {
      shown: shown,
    };
    return this.http
      .post<Response>(routes.notSetUserNotificationsShownState, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public adminListNotifications() {
    const body = {};
    return this.http
      .post<Response>(routes.notAdminListNotifications, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public insertOrUpdateNotification(notification: NotificationsDto) {
    const id = notification.id ? notification.id : '';
    const body = {
      notificationId: id,
      title: notification.title,
      typeIcon: notification.typeIcon,
      dateCreated: notification.dateCreated,
      active: notification.active,
      notes: notification.notes,
      link: notification.link,
    };
    return this.http
      .post<Response>(routes.notAdminInsertNotification, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  // STATISTICHE

  /**
   * Aggiungi gruppo di statistiche a firestore db
   * param operations
   */
  public addStatRecords(operations: any): any {
    console.log('editor: addStatRecords');
    console.log(operations);
    const body = {
      operations: operations,
    };
    return this.http.post<Response>(routes.staAddStatRecords, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  /**
   * Ottieni numero statistiche per intervallo di tempo, utente e tipo di operzione
   * param email
   * param startDate
   * param endDate
   * param idOperation
   */
  public getStatRecords(
    email: string,
    startDate: any,
    endDate: any,
    idOperation: string
  ): any {
    const body = {
      email: email,
      startDate: startDate,
      endDate: endDate,
      idOperation: idOperation,
    };
    /*
    return this.http.post(routes.getStatRecords, body, {})
      .pipe(
        map(
          (res: Response) => res
        ),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        }));
        */
  }

  /**
   * Ottieni lista delle statistiche per intervallo di tempo e utente
   * param email
   * param startDate
   * param endDate
   */
  public readSingleUserStats(
    email: string,
    startDate: string,
    endDate: string
  ): any {
    const body = {
      email: email,
      startDate: startDate,
      endDate: endDate,
    };
    return this.http
      .post<Response>(routes.staReadSingleUserStats, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  /**
   * Ottieni lista delle statistiche per intervallo di tempo e ordine
   * param orderId
   * param startDate
   * param endDate
   */
  public readOrderStatsStep1(
    orderId: string,
    startDate: string,
    endDate: string
  ): any {
    const body = {
      orderId: orderId,
      startDate: startDate,
      endDate: endDate,
    };
    return this.http
      .post<Response>(routes.staReadOrderStatsStep1, body, {}) // headers: new HttpHeaders({ timeout: `${5400000}` }) })
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public readOrderStatsStep2(
    orderId: string,
    startDate: string,
    endDate: string
  ): any {
    const body = {
      orderId: orderId,
      startDate: startDate,
      endDate: endDate,
    };
    return this.http
      .post<Response>(routes.staReadOrderStatsStep2, body, {}) // headers: new HttpHeaders({ timeout: `${5400000}` }) })
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public readOrderStatsStep3(
    orderId: string,
    startDate: string,
    endDate: string
  ): any {
    const body = {
      orderId: orderId,
      startDate: startDate,
      endDate: endDate,
    };
    return this.http
      .post<Response>(routes.staReadOrderStatsStep3, body, {}) // headers: new HttpHeaders({ timeout: `${5400000}` }) })
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public readOrderStatsStep4(orderId: string): any {
    const body = {
      orderId: orderId,
    };
    return this.http
      .post<Response>(routes.staReadOrderStatsStep4, body, {}) // headers: new HttpHeaders({ timeout: `${5400000}` }) })
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  /**
   * Registra statistica custom (evento scatenato da altro utente)
   * param docId
   * param idOperation
   * param userEmail
   */
  public addCustomDocStat(
    docId: string,
    idOperation: string,
    userEmail: string
  ): any {
    const bucketName = this.firebaseService.getBucketName();
    const body = {
      bucket: bucketName,
      docId: docId,
      idOperation: idOperation,
      userEmail: userEmail,
    };

    const options = { headers: new HttpHeaders({ Authorization: '' }) };

    return this.http
      .post<Response>(routes.staAddCustomDocStat, body, options)
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public freezeSubscription(year: number, month: number): any {
    const body = {
      year: year,
      month: month,
    };
    return this.http
      .post<Response>(routes.staFreezeSubscription, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  // ----------------------------------------------------------------------------------
  public getStatsByOriginAndYearStep1(origin: string, year: number) {
    const body = {
      origin: origin,
      year: year,
    };
    return this.http
      .post<Response>(routes.staGetStatsByOriginAndYearStep1, body, {})
      .pipe(
        timeout(180000),
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public getStatsByOriginAndYearStep2(year: number) {
    const body = {
      year: year,
    };
    return this.http
      .post<Response>(routes.staGetStatsByOriginAndYearStep2, body, {})
      .pipe(
        timeout(180000),
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public getStatsByOriginAndYearStep3(year: number) {
    const body = {
      year: year,
    };
    return this.http
      .post<Response>(routes.staGetStatsByOriginAndYearStep3, body, {})
      .pipe(
        timeout(180000),
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public getStatsByOriginAndYearStep4(year: number) {
    const body = {
      year: year,
    };
    return this.http
      .post<Response>(routes.staGetStatsByOriginAndYearStep4, body, {})
      .pipe(
        timeout(180000),
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public getStatsByOriginAndYearStep5(
    subs: any,
    totUsers: any,
    activeUsers: any,
    openedMaps: any
  ) {
    const body = {
      subs: subs,
      totUsers: totUsers,
      activeUsers: activeUsers,
      openedMaps: openedMaps,
    };
    return this.http
      .post<Response>(routes.staGetStatsByOriginAndYearStep5, body, {})
      .pipe(
        timeout(180000),
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  // ----------------------------------------------------------------------------------

  public getDomainsStatsByYear(year: number) {
    const body = {
      year: year,
    };
    return this.http
      .post<Response>(routes.staGetDomainsStatsByYear, body, {})
      .pipe(
        timeout(540000),
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public findDocSharedInfo(docId: string) {
    const body = {
      docId: docId,
    };
    return this.http.post<Response>(routes.docFindDocSharedInfo, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public writeShares(docId: string, shares: any) {
    const body = {
      docId: docId,
      shares: shares,
    };
    return this.http.post<Response>(routes.docWriteShares, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public findOwnerEmailByDocId(docId: string) {
    const body = {
      docId: docId,
    };
    return this.http
      .post<Response>(routes.docFindOwnerEmailByDocId, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public deleteSubscription(orderId: string) {
    const body = {
      orderId: orderId,
    };
    return this.http
      .post<Response>(routes.subDeleteSubscription, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public findDocLinkInfo(origin: string, googleId: string) {
    const body = {
      origin: origin,
      googleId: googleId,
    };
    return this.http.post<Response>(routes.docFindDocLinkInfo, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public addUserToNewsletter(fullName: string, email: string, interests: any) {
    const body = {
      fullName: fullName,
      email: email,
      interests: interests,
    };
    return this.http
      .post<Response>(routes.usrAddUserToNewsletter, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public activateDemo(fullName: string, email: string) {
    const body = {
      fullName: fullName,
      email: email,
    };
    return this.http.post<Response>(routes.subActivateDemo, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  // public unlockOrders(orderIds: Array<string>) {
  //   const body = {
  //     orderIds: orderIds,
  //   };
  //   return this.http.post<Response>(routes.subUnlockOrders, body, {}).pipe(
  //     map((res: Response) => res),
  //     catchError((error: any) => {
  //       logger.error(error);
  //       return observableThrowError(() => error);
  //     })
  //   );
  // }

  /**
   * Call the 'recursiveDelete' callable function with a path to initiate
   * a server-side delete.
   */
  public deleteMapEvents(
    deletionType: string,
    docId: string,
    toDateTime: string
  ) {
    const body = {
      deletionType: deletionType,
      docId: docId,
      toDateTime: toDateTime,
    };
    return this.http.post<Response>(routes.mapDeleteMapEvents, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
    // const q = query(citiesRef, orderBy("state"), orderBy("population", "desc"));
    // const q = query(collectionRef, orderBy('__name__'), limit(batchSize));
    // return new Promise((resolve, reject) => {
    //     this.deleteQueryBatch(db, q, resolve).catch(reject);
    // });
  }

  /**
   * Call the 'recursiveDelete' callable function with a path to initiate
   * a server-side delete.
   */
  public deleteChat(docId: string, batchSize: number) {
    const body = {
      docId: docId,
      batchSize: batchSize,
    };
    return this.http.post<Response>(routes.docDeleteChat, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
    // const q = query(citiesRef, orderBy("state"), orderBy("population", "desc"));
    // const q = query(collectionRef, orderBy('__name__'), limit(batchSize));
    // return new Promise((resolve, reject) => {
    //     this.deleteQueryBatch(db, q, resolve).catch(reject);
    // });
  }

  public latex2image(latex1: string, latex2: string) {
    const body = {
      latex1: latex1,
      latex2: latex2,
    };
    return this.http.post<Response>(routes.matLatex2Image, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public getOcrMathPix(
    email: string,
    imageBase64: string,
    latexFormat: boolean
  ) {
    const body = {
      email: email,
      imageBase64: imageBase64,
      latexFormat: latexFormat,
    };
    return this.http.post<Response>(routes.matGetOcrMathpix, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public getOcrMathPixScanCount(email: string) {
    const body = {
      email: email,
    };
    return this.http
      .post<Response>(routes.matGetOcrMathpixScanCount, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public getOcrMathPixScansLeft(subscriptionId: string) {
    const body = {
      subscriptionId: subscriptionId,
    };
    return this.http
      .post<Response>(routes.matGetOcrMathpixScansLeft, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public listUsers(searchText: string) {
    const body = {
      searchText: searchText,
    };
    return this.http.post<Response>(routes.usrListUsers, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public deleteUserByEmail(email: string, deleteData: boolean) {
    const body = {
      email: email,
      deleteData: deleteData,
    };
    return this.http.post<Response>(routes.usrDeleteUserByEmail, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public uploadDocToDrive(docId: string, folderId: string) {
    const body = {
      docId: docId,
      folderId: folderId,
    };
    return this.http.post<Response>(routes.docUploadDocToDrive, body, {}).pipe(
      map((res: Response) => res),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }

  public parseErrorFromGoogleDrive(error: any): string {
    let errorString = '';
    if (error.allErrors != null) {
      error.allErrors.array.forEach((err: any) => {
        switch (err.reason) {
          case 'authError':
            // Invalid Credentials
            errorString = 'AUTH_ERROR';
            break;
          case 'activeItemCreationLimitExceeded':
            // This account has exceeded the creation limit of 500 million items. To create more items, permanently delete some items.
            errorString = 'ACTIVE_ITEM_CREATION_LIMIT_EXCEEDED';
            break;
          case 'dailyLimitExceeded':
            // Daily Limit Exceeded
            errorString = 'DAILY_LIMIT_EXCEEDED';
            break;
          case 'fileOwnerNotMemberOfTeamDrive':
            // Cannot move a file into a shared drive as a writer when the owner of the file is not a member of that shared drive.
            errorString = 'FILE_OWNER_NOT_MEMEBER_OF_TEAM_DRIVE';
            break;
          case 'insufficientFilePermissions':
            // The user does not have sufficient permissions for file
            errorString = 'INSUFFICIENT_FILE_PERMISSIONS';
            break;
          case 'myDriveHierarchyDepthLimitExceeded':
            // Your My Drive can't contain more than 100 levels of folders.
            errorString = 'DRIVE_HIERARCHY_DEPTH_LIMIT_EXCEEDED';
            break;
          case 'storageQuotaExceeded':
            // The user's Drive storage quota has been exceeded.
            errorString = 'STORAGE_QUOTA_EXCEEDED';
            break;
          case 'teamDriveFileLimitExceeded':
            // The file limit for this shared drive has been exceeded.
            errorString = 'TEAM_DRIVE_FILE_LIMIT_EXCEEDED';
            break;
          case 'teamDriveMembershipRequired':
            // The attempted action requires shared drive membership.
            errorString = 'TEAM_DRIVE_MEMBERSHIP_REQUIRED';
            break;
          case 'userRateLimitExceeded':
            // User Rate Limit Exceeded.
            errorString = 'USER_RATE_LIMIT_EXCEEDED';
            break;
        }
      });
    }

    return errorString;
  }

  public emptyTrash() {
    return this.http.post(routes.docEmptyTrash, {}).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        logger.error(error);
        return observableThrowError(() => error);
      })
    );
  }


  public addDocMetadata(docId: string, docName: string, docType: string, docLanguage: string): Observable<any> {
    const body = {
      docId: docId,
      docName: docName,
      docType: docType,
      docLanguage: docLanguage
    };
    return this.http
      .post<Response>(routes.docAddDocMetadata, body, {})
      .pipe(
        map((res: Response) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public findDocInfoByDocId(docId: string): Observable<any> {
    const body = {
      docId: docId
    };
    return this.http
      .post<Response>(routes.docFindDocInfoByDocId, body, {})
      .pipe(
        map((res: any) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }

  public changeDocLanguage(docId: string, language: string): Observable<any> {
    const body = {
      docId: docId,
      language: language
    };
    return this.http
      .post<Response>(routes.docChangeDocLanguage, body, {})
      .pipe(
        map((res: any) => res),
        catchError((error: any) => {
          logger.error(error);
          return observableThrowError(() => error);
        })
      );
  }


  public downloadFromStorageAsBlob(resourceFullPath: string, typeBlob: string): Observable<any> {
    return this.firebaseService.downloadResourceAsBlob(resourceFullPath, typeBlob);
  }

  public downloadFromStorage(resourceFullPath: string): Observable<any> {
    return this.firebaseService.downloadResource(resourceFullPath);
  }

}